import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Alert, AlertActionCloseButton,
  Button, Popover,
  Dropdown, DropdownItem, DropdownList, MenuToggle,
  SearchInput,
} from '@patternfly/react-core';
import { ChartDonut, ChartLabel } from '@patternfly/react-charts';
import Timestamp from 'react-timestamp';
import { applyFilter } from '@app/utils/parseSearchString';

import { NamespacesTable } from './NamespacesTable';

import { ExclamationTriangleIcon, DownloadIcon } from '@patternfly/react-icons';
import { useNamespaceExport } from './useNamespaceExport';
import { useClientEvent } from '@app/hooks/useClientEvent';
import { useMsal } from '@azure/msal-react';

import { CLUSTERS, GRANULAR } from '@app/config';
import { isClusterSelectable, RESTRICTED_CLUSTER_REASON } from '@app/GranularDeploy/helpers/clusterConfig';

import '@patternfly/react-core/dist/styles/base.css';

import { SimpleSelect, SimpleSelectOption } from '@patternfly/react-templates';
import LoadingError from './LoadingError';
import LoadingSpin from './LoadingSpin';

import { Namespace } from './models';
import { Link } from 'react-router-dom';

import {
  useNamespaces,
  useAutoshutdown,
  useQuickDeployPermission,
  Metadata,
  AutoshutdownStatus,
} from './hooks/useDashboardData';


globalThis.cluster_selected_value = localStorage.getItem("cluster_selected")
if (globalThis.cluster_selected_value == null || !Object.keys(CLUSTERS).includes(globalThis.cluster_selected_value)) {
  globalThis.cluster_selected_value = Object.values(CLUSTERS)[0].CLUSTER_NAME
  localStorage.setItem("cluster_selected", globalThis.cluster_selected_value);
  window.location.reload();
}

globalThis.cluster = CLUSTERS[globalThis.cluster_selected_value]

const Options: SimpleSelectOption[] = Object.values(CLUSTERS).map(cluster => {
  const selectable = isClusterSelectable(cluster.CLUSTER_NAME)
  return {
    content: selectable ? cluster.CLUSTER_NAME : <span style={{ opacity: 0.5 }}>{cluster.CLUSTER_NAME}</span>,
    value: cluster.CLUSTER_NAME,
    isDisabled: !selectable,
    description: selectable ? undefined : <span style={{ display: 'block', whiteSpace: 'normal', maxWidth: '240px' }}>{RESTRICTED_CLUSTER_REASON}</span>,
  }
})

export const SelectSimpleDemo: React.FunctionComponent = () => {
  const [isDisabled, setIsDisabled] = React.useState<boolean>(false);
  const [selected, setSelected] = React.useState<string | undefined>(globalThis.cluster.CLUSTER_NAME);

  const initialOptions = React.useMemo<SimpleSelectOption[]>(
    () => Options.map((o) => ({ ...o, selected: o.value === selected })),
    [selected]
  );

  const onSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: any | number | undefined) => {
    localStorage.setItem('cluster_selected', value)
    sessionStorage.setItem('cluster_value', value)
    window.location.reload();
  };

  return (
    <React.Fragment>
      <SimpleSelect
        initialOptions={initialOptions}
        isDisabled={isDisabled}
        onSelect={onSelect}
        selected={setSelected}
        popperProps={{ position: 'right' }}
      />
    </React.Fragment>
  );
};

interface NamespaceFilteringProps {
  onSearch: CallableFunction;
  value: string | null;
  resultsCount: number;
}

const NamespaceFiltering: React.FunctionComponent<NamespaceFilteringProps> = ({ value, onSearch, resultsCount }) => {

  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    if (value != null) {
      setSearch(value)
    }
  }, [value])

  return (
    <>
      <SearchInput
        attributes={[
          { attr: 'name', display: 'Name' },
          { attr: 'creator', display: 'Creator' },
          { attr: 'team', display: 'Team' },
          { attr: 'status', display: 'Status' },
          { attr: 'commit', display: 'Image name/version' }
        ]}
        advancedSearchDelimiter=":"
        aria-label="name filter"
        placeholder="Filter by fields, eg name:<namespace> creator:<email> team:<team> status:<status> idle:<true|false> commit:<image name|version>"
        value={search}
        onChange={(_event, _value) =>
          setSearch(_value)
        }
        onClear={(_event) => {
          setSearch("")
          onSearch("")
        }}
        onSearch={(_event, _value) => {
          setSearch(_value)
          onSearch(_value)
        }
        }
        resultsCount={resultsCount}
      />
    </>
  )
}

interface ClusterInformationProps {
  lastUpdate: Date;
  metadata: Metadata;
  autoshutdowndata: AutoshutdownStatus;
  namespaces: Namespace[];
}

const ClusterInformation: React.FunctionComponent<ClusterInformationProps> = ({ lastUpdate, metadata, autoshutdowndata, namespaces }) => {

  const [search, setSearch] = useState<string | null>(null);
  const [filtered, setFiltered] = useState<Namespace[]>([]);

  const { accounts } = useMsal();
  const fireEvent = useClientEvent();

  const { exportNamespaces, exporting, exportError, clearExportError } = useNamespaceExport();
  const [exportPeriod, setExportPeriod] = useState<string>('all');
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);

  const exportPeriodOptions: { key: string; label: string; days: number | null }[] = [
    { key: '24h', label: 'Last 24 hours', days: 1 },
    { key: '7d', label: 'Last 7 days', days: 7 },
    { key: '30d', label: 'Last 30 days', days: 30 },
    { key: 'all', label: 'All time', days: null },
  ];

  const selectedPeriodLabel = exportPeriodOptions.find((o) => o.key === exportPeriod)?.label ?? 'All time';

  const handleExport = () => {
    const option = exportPeriodOptions.find((o) => o.key === exportPeriod);
    let toExport = filtered;
    if (option?.days != null) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - option.days);
      toExport = filtered.filter((ns) => ns.date && new Date(ns.date) >= cutoff);
    }
    fireEvent('ENV_NAMESPACE_EXPORTED', { period: exportPeriod });
    exportNamespaces(toExport, selectedPeriodLabel);
  };

  useEffect(() => {
    let savedSearch = sessionStorage.getItem('search');
    if (savedSearch == null) {
      savedSearch = localStorage.getItem('search');
    }

    if (savedSearch != null) {
      setSearch(savedSearch);
    } else if (accounts[0]) {
      setSearch('creator:' + accounts[0].idTokenClaims?.preferred_username);
    }

  }, [])

  useEffect(() => {
    if (search == null) {
      return;
    }

    if (search != "") {
      const filteredRows = applyFilter(namespaces, search);
      setFiltered(filteredRows);
    } else {
      setFiltered(namespaces);
    }

    if (sessionStorage.getItem('search') != search) {
      sessionStorage.setItem('search', search);
    }

    if (localStorage.getItem('search') != search) {
      localStorage.setItem("search", search);
    }


  }, [search, namespaces])

  return (
    <>
      <div className="dx-stack dx-stack--gutter">
        {/* Details card */}
        <div className="env-details-card">
          <div className="env-details-left">
            <div className="env-detail-item">
              <label>Cluster</label>
              <span>{globalThis.cluster.CLUSTER_NAME.toUpperCase()}</span>
            </div>
            <div className="env-detail-item">
              <label>Last data fetch</label>
              <span><Timestamp date={new Date(lastUpdate)} options={{ includeDay: false }} /></span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {!(autoshutdowndata?.success) && (
              <Alert variant="warning" title="Autoshutdown" isInline isPlain>
                <a target='_blank' rel='noopener noreferrer' href='https://teladochealth.atlassian.net/wiki/x/I1TqCw'>Learn more.</a>
              </Alert>
            )}

            <div style={{ textAlign: 'center' }}>
              <div style={{ height: '150px', width: '200px' }}>
                <ChartDonut
                  constrainToVisibleArea
                  height={150}
                  width={200}
                  data={[{ x: 'Running', y: metadata?.namespaces.running }, { x: 'Stale', y: metadata?.namespaces.stale}, { x: 'Outdated', y: metadata?.namespaces.outdated }, { x: 'Warning', y: metadata?.namespaces.warning }, { x: 'Failing', y: metadata?.namespaces.failing }, { x: 'Updating', y: metadata?.namespaces.updating }, { x: 'Patching', y: metadata?.namespaces.patching }, { x: 'Stopped', y: metadata?.namespaces.stopped }, { x: 'Unknown', y: metadata?.namespaces.unknown }]}
                  colorScale={["#4CB140", "#F9E0A2", "#F9E0A2", "#EF9234", "#C9190B", "#519DE9", "#8481DD", "#F0F0F0", "#73C5C5"]}
                  labels={({ datum }) => `${datum.x}: ${datum.y}`}
                  name="namespaces-chart"
                  subTitle="Environments"
                  subTitleComponent={<ChartLabel style={{ fill: 'var(--text-secondary)' }} />}
                  title={metadata?.namespaces.total ? metadata?.namespaces.total.toString() : "0"}
                  titleComponent={<ChartLabel style={{ fill: 'var(--text-primary)' }} />}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Environments card */}
        <div className="env-section">
          <div className="env-section-header">
            <h2 className="env-section-title">Environments</h2>
            <div className="dx-flex dx-flex--gap-sm" style={{ alignItems: 'center' }}>
              <Dropdown
                isOpen={isExportDropdownOpen}
                onOpenChange={(open) => setIsExportDropdownOpen(open)}
                toggle={(toggleRef) => (
                  <MenuToggle ref={toggleRef} onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)} isExpanded={isExportDropdownOpen} variant="plain">
                    {selectedPeriodLabel}
                  </MenuToggle>
                )}
              >
                <DropdownList>
                  {exportPeriodOptions.map((option) => (
                    <DropdownItem
                      key={option.key}
                      isSelected={exportPeriod === option.key}
                      onClick={() => { setExportPeriod(option.key); setIsExportDropdownOpen(false); }}
                    >
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </Dropdown>
              <Button
                variant="secondary"
                icon={<DownloadIcon />}
                isLoading={exporting}
                isDisabled={exporting || filtered.length === 0}
                onClick={handleExport}
              >
                Export to Excel
              </Button>
              {GRANULAR && (
                <>
                  {localStorage.getItem('quick_deploy_enabled') === 'true' ? (
                    <Button variant="primary" component={(props: any) => <Link {...props} to="/granular-deploy" />}>
                      Manage Environment
                    </Button>
                  ) : (
                    <Popover
                      position="top"
                      headerContent="Access Denied"
                      bodyContent="You do not have the required permissions to create a new environment."
                      alertSeverityVariant="warning"
                      headerIcon={<ExclamationTriangleIcon />}
                    >
                      <Button variant="primary" isAriaDisabled>
                        Manage Environment
                      </Button>
                    </Popover>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="env-search-bar">
            <NamespaceFiltering value={search} onSearch={(value: string) => setSearch(value)} resultsCount={filtered.length} />
          </div>

          {exportError && (
            <div style={{ padding: '0 20px 16px' }}>
              <Alert
                variant="danger"
                isInline
                title="Export failed"
                actionClose={<AlertActionCloseButton onClose={clearExportError} />}
              >
                {exportError}
              </Alert>
            </div>
          )}

          <NamespacesTable namespaces={filtered} clearFilters={() => setSearch("")} />
        </div>
      </div>
    </>
  )
}


const Dashboard: React.FunctionComponent<{}> = () => {
  const { namespaces, lastUpdate, metadata, isLoading: nsLoading, error: nsError } = useNamespaces();
  const { autoshutdowndata, isLoading: autoLoading } = useAutoshutdown();
  useQuickDeployPermission();

  const isLoading = nsLoading || autoLoading;

  return (
    <>
      <div className="env-page-header">
        <h1 className="env-page-title">
          {globalThis.cluster.CLUSTER_NAME.toUpperCase()} Environments Dashboard
        </h1>
        {Object.keys(CLUSTERS).length > 1 ? <SelectSimpleDemo /> : undefined}
      </div>
      <hr className="dx-divider" />
      {isLoading ? (
        <LoadingSpin message="" />
      ) : nsError ? (
        <section className="dx-page-section">
          <LoadingError title="Something went wrong">{nsError}</LoadingError>
        </section>
      ) : (
        <ClusterInformation
          lastUpdate={lastUpdate!}
          metadata={metadata}
          autoshutdowndata={autoshutdowndata}
          namespaces={namespaces}
        />
      )}
    </>
  );
}

export { Dashboard };
